import {
    Benefit,
    Listing,
    ListingOption,
    PermissionEvidence,
    ProductCluster
} from './domain';
import {
    PlatformAdapter,
    PlatformAdapterKind,
    PlatformListingDetail,
    PlatformPriceAndStock,
    PlatformRawListingBase,
    PlatformSearchProductsRequest,
    PlatformSearchProductsResult
} from './platform-adapter';
import {
    lookitListings,
    lookitProductClusters
} from './mock-data';
import { calculatePublicEffectivePrice, getPublicPriceBreakdown, normalizeListingFreshness } from './price-utils';
import {
    PermissionGuardedPlatformAdapterRegistry,
    PlatformAdapterActivation,
    PlatformAdapterRegistration,
    PlatformPermissionRequirements,
    PlatformProviderMode
} from './platform-permission';

export interface MockPlatformRawPayload {
    provider: PlatformAdapterKind;
    source_name: string;
    normalized_listing_id: string;
    platform_product_key: string;
    fields: Record<string, unknown>;
}

export interface MockPlatformRawListing extends PlatformRawListingBase<MockPlatformRawPayload> {
    normalized_listing: Listing;
    mock_rank: number;
}

interface MockProviderConfig {
    platform_id: string;
    platform_name: string;
    platform_kind: PlatformAdapterKind;
}

export const authorizedMockProviderPlatformId = 'platform-authorized-mock-provider';
export const authorizedMockProviderName = 'authorizedMockProvider';

const authorizedMockProviderConfig: MockProviderConfig = {
    platform_id: authorizedMockProviderPlatformId,
    platform_name: authorizedMockProviderName,
    platform_kind: 'personal_mall'
};

const mockProviderConfigs: MockProviderConfig[] = [
    { platform_id: 'platform-musinsa', platform_name: '무신사', platform_kind: 'musinsa' },
    { platform_id: 'platform-29cm', platform_name: '29CM', platform_kind: '29cm' },
    { platform_id: 'platform-wconcept', platform_name: 'W컨셉', platform_kind: 'wconcept' },
    { platform_id: 'platform-zigzag', platform_name: '지그재그', platform_kind: 'zigzag' },
    { platform_id: 'platform-ably', platform_name: '에이블리', platform_kind: 'ably' },
    { platform_id: 'platform-naver-smartstore', platform_name: '네이버 스마트스토어', platform_kind: 'naver' },
    { platform_id: 'platform-spa-official', platform_name: 'SPA 공식몰', platform_kind: 'spa_official' },
    { platform_id: 'platform-personal-mall', platform_name: '개인 쇼핑몰', platform_kind: 'personal_mall' },
    { platform_id: 'platform-hago', platform_name: 'HAGO', platform_kind: 'hago' },
    authorizedMockProviderConfig
];

export const authorizedMockProviderPermissionRequirements: PlatformPermissionRequirements = {
    sourceName: authorizedMockProviderName,
    sourceType: 'personal_mall_direct_contract',
    permissionType: 'direct_contract',
    requiredData: [
        'product_title',
        'product_url',
        'seller_name',
        'image_url',
        'listed_price',
        'sale_price',
        'public_coupon',
        'shipping_fee',
        'free_shipping_condition',
        'option_stock',
        'availability',
        'price_snapshot'
    ],
    requireDisplay: true,
    requirePriceStorage: true,
    requireImageDisplay: true
};

export interface AuthorizedMockProviderRuntimeConfig {
    providerMode: PlatformProviderMode;
    apiKeyConfigured: boolean;
    endpoint: string | null;
}

type RuntimeScope = {
    LOOKIT_PILOT_PLATFORM_PROVIDER_MODE?: unknown;
    LOOKIT_PILOT_PLATFORM_ENDPOINT?: unknown;
    __LOOKIT_CONFIG__?: {
        pilotPlatformProviderMode?: unknown;
        pilotPlatformEndpoint?: unknown;
    };
    process?: {
        env?: Record<string, string | undefined>;
    };
};

function normalizedProviderMode(value: unknown): PlatformProviderMode | null {
    return value === 'real' || value === 'mock' ? value : null;
}

function envValue(scope: RuntimeScope | null, key: string): string | null {
    return scope?.process?.env?.[key] || null;
}

function runtimeString(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export function runtimeAuthorizedMockProviderConfig(): AuthorizedMockProviderRuntimeConfig {
    const scope = typeof globalThis === 'undefined' ? null : globalThis as unknown as RuntimeScope;
    const appConfig = scope?.__LOOKIT_CONFIG__;
    const providerMode = normalizedProviderMode(scope?.LOOKIT_PILOT_PLATFORM_PROVIDER_MODE)
        || normalizedProviderMode(envValue(scope, 'LOOKIT_PILOT_PLATFORM_PROVIDER_MODE'))
        || normalizedProviderMode(appConfig?.pilotPlatformProviderMode)
        || 'mock';
    const endpoint = runtimeString(scope?.LOOKIT_PILOT_PLATFORM_ENDPOINT)
        || envValue(scope, 'LOOKIT_PILOT_PLATFORM_ENDPOINT')
        || runtimeString(appConfig?.pilotPlatformEndpoint);

    return {
        providerMode,
        apiKeyConfigured: Boolean(envValue(scope, 'LOOKIT_PILOT_PLATFORM_API_KEY')),
        endpoint
    };
}

function cloneData<T>(data: T): T {
    return JSON.parse(JSON.stringify(data)) as T;
}

function optionId(listingId: string, color: string, size: string): string {
    return `${listingId}::${color}::${size}`;
}

function variantId(productClusterId: string, color: string, size: string): string {
    return `${productClusterId}::${color}::${size}`;
}

function listingOption(listingId: string, productClusterId: string, color: string, size: string, stockCount: number): ListingOption {
    return {
        id: optionId(listingId, color, size),
        listing_id: listingId,
        variant_id: variantId(productClusterId, color, size),
        color,
        size,
        availability_status: stockCount <= 0 ? 'sold_out' : stockCount <= 2 ? 'low_stock' : 'in_stock',
        stock_count: stockCount,
        restock_expected_at: stockCount <= 0 ? '2026-07-08T09:00:00+09:00' : null,
        restock_alert_available: stockCount <= 0,
        last_checked_at: '2026-06-24T08:16:00+09:00',
        checked_at: '2026-06-24T08:16:00+09:00',
        collected_at: '2026-06-24T08:12:00+09:00'
    };
}

function listingBenefit(
    listingId: string,
    benefitType: Benefit['benefit_type'],
    name: string,
    amount: number | null,
    conditions: string,
    isPublicDefault: boolean
): Benefit {
    return {
        id: `benefit-${listingId}-${benefitType}`,
        listing_id: listingId,
        benefit_type: benefitType,
        name,
        amount,
        rate: null,
        conditions,
        is_public_default: isPublicDefault,
        starts_at: null,
        ends_at: null
    };
}

function clusterForListing(productClusterId: string): ProductCluster | null {
    return lookitProductClusters.find((cluster) => cluster.id === productClusterId) || null;
}

function createSpaOfficialListing(): Listing {
    const productClusterId = 'cluster-similar-wide-slacks';
    const listingId = 'listing-spa-wide-slacks-official';
    const cluster = clusterForListing(productClusterId);
    const checkedAt = '2026-06-24T08:16:00+09:00';
    const listedPrice = 39900;
    const instantDiscount = 4000;
    const publicCouponDiscount = 2000;
    const shippingFee = 0;
    const freshness = normalizeListingFreshness({
        status: 'stale',
        checkedAt,
        collectedAt: '2026-06-24T08:12:00+09:00',
        refreshInterval: 180,
        nextRefreshAt: '2026-06-24T11:16:00+09:00',
        confidence: 'medium',
        confidenceScore: 0.72,
        sourceStatus: 'mock'
    }, new Date('2026-06-26T09:00:00+09:00'));

    return {
        id: listingId,
        product_cluster_id: productClusterId,
        platform_id: 'platform-spa-official',
        seller_id: 'seller-spa-official',
        external_product_id: 'spa-wide-slacks-official',
        platform_listing_id: 'spa-wide-slacks-official',
        seller_product_id: 'sku-spa-wide-slacks',
        title: 'SPA 공식몰 핀턱 와이드 슬랙스',
        description: 'SPA 공식몰 mock 상품. 공개 할인과 기본 무료배송만 실구매가에 반영.',
        product_url: 'https://example.com/spa-official/wide-slacks',
        image_urls: cluster ? [cluster.representative_image_url] : [],
        category_root_id: cluster?.category_root_id || 'bottom',
        category_root: cluster?.category_root || '하의',
        subcategory_id: cluster?.subcategory_id || 'bottom:pants',
        subcategory: cluster?.subcategory || '팬츠',
        category_path: cluster?.category_path || {
            root_id: 'bottom',
            root_label: '하의',
            subcategory_id: 'bottom:pants',
            subcategory_label: '팬츠',
            path_label: '하의 > 팬츠'
        },
        listed_price: listedPrice,
        sale_price: listedPrice - instantDiscount,
        instant_discount: instantDiscount,
        public_coupon_discount: publicCouponDiscount,
        base_shipping_fee: 0,
        shipping_fee: shippingFee,
        free_shipping_condition: 'SPA 공식몰 기본 무료배송',
        public_effective_price: calculatePublicEffectivePrice({
            listed_price: listedPrice,
            instant_discount: instantDiscount,
            public_coupon_discount: publicCouponDiscount,
            shipping_fee: shippingFee
        }),
        currency: 'KRW',
        availability_status: 'in_stock',
        variant_ids: [
            variantId(productClusterId, '차콜', 'S'),
            variantId(productClusterId, '차콜', 'M'),
            variantId(productClusterId, '블랙', 'M')
        ],
        options: [
            listingOption(listingId, productClusterId, '차콜', 'S', 8),
            listingOption(listingId, productClusterId, '차콜', 'M', 6),
            listingOption(listingId, productClusterId, '블랙', 'M', 2)
        ],
        benefits: [
            listingBenefit(listingId, 'instant_discount', '공식몰 즉시 할인', instantDiscount, '모든 사용자 공통', true),
            listingBenefit(listingId, 'public_coupon', '공개 다운로드 쿠폰', publicCouponDiscount, '상품 페이지에서 누구나 다운로드', true),
            listingBenefit(listingId, 'shipping', '기본 무료배송', shippingFee, '단품 구매 기준', true),
            listingBenefit(listingId, 'new_user', '신규회원 웰컴 쿠폰', 5000, '신규 가입 사용자 한정', false)
        ],
        last_checked_at: checkedAt,
        checked_at: checkedAt,
        collected_at: '2026-06-24T08:12:00+09:00',
        confidence: 0.72,
        freshness,
        source_status: 'mock',
        price_note: '개인 신규가입 쿠폰은 참고 혜택으로만 표시'
    };
}

function createAuthorizedMockProviderListing(): Listing {
    const productClusterId = 'cluster-liette-lace-blouse';
    const listingId = 'listing-authorized-mock-liette-lace-blouse';
    const cluster = clusterForListing(productClusterId);
    const checkedAt = '2026-06-28T09:15:00+09:00';
    const collectedAt = '2026-06-28T09:12:00+09:00';
    const listedPrice = 42900;
    const instantDiscount = 3000;
    const publicCouponDiscount = 2000;
    const shippingFee = 3000;
    const freshness = normalizeListingFreshness({
        status: 'fresh',
        checkedAt,
        collectedAt,
        refreshInterval: 120,
        nextRefreshAt: '2026-06-28T11:15:00+09:00',
        confidence: 'medium',
        confidenceScore: 0.76,
        sourceStatus: 'mock'
    }, new Date('2026-06-28T09:20:00+09:00'));

    return {
        id: listingId,
        product_cluster_id: productClusterId,
        platform_id: authorizedMockProviderPlatformId,
        seller_id: 'seller-authorized-mock-pilot',
        external_product_id: 'authorized-mock-liette-lace-blouse',
        platform_listing_id: 'authorized-mock-liette-lace-blouse',
        seller_product_id: 'pilot-sku-liette-lace-blouse',
        title: 'authorizedMockProvider 리에뜨 레이스 블라우스',
        description: '권한 guard 검증용 mock-authorized 파일럿 상품. 실제 외부 호출이나 parser 단정은 없다.',
        product_url: 'https://example.com/authorized-mock/liette-lace-blouse',
        image_urls: cluster ? [cluster.representative_image_url] : [],
        category_root_id: cluster?.category_root_id || 'top',
        category_root: cluster?.category_root || '상의',
        subcategory_id: cluster?.subcategory_id || 'top:blouse',
        subcategory: cluster?.subcategory || '블라우스',
        category_path: cluster?.category_path || {
            root_id: 'top',
            root_label: '상의',
            subcategory_id: 'top:blouse',
            subcategory_label: '블라우스',
            path_label: '상의 > 블라우스'
        },
        listed_price: listedPrice,
        sale_price: listedPrice - instantDiscount,
        instant_discount: instantDiscount,
        public_coupon_discount: publicCouponDiscount,
        base_shipping_fee: shippingFee,
        shipping_fee: shippingFee,
        free_shipping_condition: '50,000원 이상 무료배송',
        free_shipping_price_basis: 'after_public_coupon',
        public_effective_price: calculatePublicEffectivePrice({
            listed_price: listedPrice,
            instant_discount: instantDiscount,
            public_coupon_discount: publicCouponDiscount,
            shipping_fee: shippingFee
        }),
        currency: 'KRW',
        availability_status: 'in_stock',
        variant_ids: [
            variantId(productClusterId, '아이보리', 'FREE'),
            variantId(productClusterId, '블랙', 'FREE')
        ],
        options: [
            listingOption(listingId, productClusterId, '아이보리', 'FREE', 5),
            listingOption(listingId, productClusterId, '블랙', 'FREE', 2)
        ],
        benefits: [
            listingBenefit(listingId, 'instant_discount', '파일럿 공개 즉시 할인', instantDiscount, '모든 사용자 공통', true),
            listingBenefit(listingId, 'public_coupon', '파일럿 공개 쿠폰', publicCouponDiscount, '상품 페이지에서 누구나 다운로드', true),
            listingBenefit(listingId, 'shipping', '단품 기준 기본 배송비', shippingFee, '기본 배송지 기준', true),
            listingBenefit(listingId, 'membership', '회원 등급 적립', 1000, '개인 등급별 상이', false)
        ],
        last_checked_at: checkedAt,
        checked_at: checkedAt,
        collected_at: collectedAt,
        confidence: 0.76,
        freshness,
        source_status: 'mock',
        price_note: '실제 파일럿 계약과 응답 샘플 확보 전까지 mock-authorized provider로만 사용'
    };
}

function confidenceForListing(listing: Listing): number {
    if (typeof listing.confidence === 'number' && Number.isFinite(listing.confidence)) return listing.confidence;
    const cluster = clusterForListing(listing.product_cluster_id);
    if (typeof cluster?.confidence_level === 'number') return cluster.confidence_level;
    return 0.7;
}

function rawPayloadForListing(config: MockProviderConfig, listing: Listing): MockPlatformRawPayload {
    const optionStock = listing.options.map((option) => ({
        optionId: option.id,
        color: option.color,
        size: option.size,
        status: option.availability_status,
        stock: option.stock_count ?? null
    }));

    const shared = {
        url: listing.product_url,
        images: listing.image_urls,
        optionStock
    };

    const providerFields: Record<string, Record<string, unknown>> = {
        musinsa: {
            goodsNo: listing.platform_listing_id,
            goodsName: listing.title,
            normalPrice: listing.listed_price,
            salePrice: listing.sale_price,
            musinsaDelivery: listing.free_shipping_condition,
            ...shared
        },
        '29cm': {
            itemNo: listing.platform_listing_id,
            itemName: listing.title,
            consumerPrice: listing.listed_price,
            couponDiscountPrice: listing.public_coupon_discount,
            deliveryPolicy: listing.free_shipping_condition,
            ...shared
        },
        wconcept: {
            productCode: listing.platform_listing_id,
            productName: listing.title,
            sellPrice: listing.sale_price,
            downloadableCoupon: listing.public_coupon_discount,
            deliveryText: listing.free_shipping_condition,
            ...shared
        },
        zigzag: {
            catalogId: listing.platform_listing_id,
            shopProductName: listing.title,
            displayPrice: listing.listed_price,
            appCouponAmount: listing.public_coupon_discount,
            shippingEvent: listing.free_shipping_condition,
            ...shared
        },
        ably: {
            goodsSno: listing.platform_listing_id,
            name: listing.title,
            price: listing.listed_price,
            marketCoupon: listing.public_coupon_discount,
            shippingFee: listing.shipping_fee,
            ...shared
        },
        naver: {
            nvMid: listing.platform_listing_id,
            productTitle: listing.title,
            lowPrice: listing.listed_price,
            storeCoupon: listing.public_coupon_discount,
            deliveryFee: listing.shipping_fee,
            ...shared
        },
        spa_official: {
            sku: listing.seller_product_id,
            productName: listing.title,
            retailPrice: listing.listed_price,
            promotionPrice: listing.sale_price,
            publicCoupon: listing.public_coupon_discount,
            deliveryPolicy: listing.free_shipping_condition,
            ...shared
        },
        personal_mall: {
            mallProductId: listing.seller_product_id,
            productName: listing.title,
            listedPrice: listing.listed_price,
            ownMallCoupon: listing.public_coupon_discount,
            shippingFee: listing.shipping_fee,
            freeShippingText: listing.free_shipping_condition,
            ...shared
        },
        hago: {
            goodsId: listing.platform_listing_id,
            goodsName: listing.title,
            originalPrice: listing.listed_price,
            benefitPrice: listing.sale_price,
            deliveryPolicy: listing.free_shipping_condition,
            ...shared
        }
    };

    const fields = providerFields[config.platform_kind] || {
        productId: listing.platform_listing_id,
        productName: listing.title,
        price: listing.listed_price,
        ...shared
    };
    const authorizedMockFields = config.platform_id === authorizedMockProviderPlatformId
        ? {
            ...fields,
            providerName: authorizedMockProviderName,
            integrationMode: 'mock_authorized',
            parserStatus: 'sample_payload_unavailable',
            permissionBoundary: 'PermissionEvidence required before real provider mode'
        }
        : fields;

    return {
        provider: config.platform_kind,
        source_name: config.platform_name,
        normalized_listing_id: listing.id,
        platform_product_key: listing.platform_listing_id || listing.id,
        fields: authorizedMockFields
    };
}

function standardListingToRaw(config: MockProviderConfig, listing: Listing, mockRank: number): MockPlatformRawListing {
    const checkedAt = listing.checked_at || listing.last_checked_at;
    const collectedAt = listing.collected_at || checkedAt;

    return {
        id: listing.id,
        platform_id: listing.platform_id,
        platform_kind: config.platform_kind,
        platform_listing_id: listing.platform_listing_id || listing.id,
        seller_id: listing.seller_id,
        seller_product_id: listing.seller_product_id,
        external_product_id: listing.external_product_id,
        product_cluster_id: listing.product_cluster_id,
        title: listing.title,
        product_url: listing.product_url,
        image_urls: cloneData(listing.image_urls),
        checked_at: checkedAt,
        collected_at: collectedAt,
        freshness: cloneData(listing.freshness),
        source_status: listing.source_status,
        confidence: confidenceForListing(listing),
        price: {
            listed_price: listing.listed_price,
            sale_price: listing.sale_price,
            instant_discount: listing.instant_discount,
            public_coupon_discount: listing.public_coupon_discount,
            base_shipping_fee: listing.base_shipping_fee,
            shipping_fee: listing.shipping_fee,
            free_shipping_condition: listing.free_shipping_condition,
            free_shipping_price_basis: listing.free_shipping_price_basis || null,
            public_effective_price: listing.public_effective_price,
            currency: listing.currency
        },
        stock: {
            availability_status: listing.availability_status,
            options: cloneData(listing.options)
        },
        raw_payload: rawPayloadForListing(config, listing),
        normalized_listing: cloneData(listing),
        mock_rank: mockRank
    };
}

function listingMatchesQuery(raw: MockPlatformRawListing, query?: string | null): boolean {
    const normalizedQuery = (query || '').trim().toLowerCase();
    if (!normalizedQuery) return true;

    const cluster = clusterForListing(raw.product_cluster_id);
    const searchable = [
        raw.title,
        raw.platform_listing_id,
        raw.seller_product_id || '',
        raw.normalized_listing.description,
        cluster?.canonical_name || '',
        cluster?.brand_name || '',
        cluster?.wholesale_tag_name || ''
    ].join(' ').toLowerCase();

    return searchable.includes(normalizedQuery);
}

function matchesSearchRequest(raw: MockPlatformRawListing, request: PlatformSearchProductsRequest): boolean {
    const productClusterIds = request.productClusterIds ? new Set(request.productClusterIds) : null;
    const listingIds = request.listingIds ? new Set(request.listingIds) : null;

    if (productClusterIds && !productClusterIds.has(raw.product_cluster_id)) return false;
    if (listingIds && !listingIds.has(raw.id) && !listingIds.has(raw.platform_listing_id)) return false;
    if (!request.includeSoldOut && raw.stock.availability_status === 'sold_out') return false;
    return listingMatchesQuery(raw, request.query);
}

export class MockPlatformAdapter implements PlatformAdapter<MockPlatformRawListing> {
    public readonly platform_id: string;
    public readonly platform_name: string;
    public readonly platform_kind: PlatformAdapterKind;
    private readonly rawListings: MockPlatformRawListing[];

    constructor(config: MockProviderConfig, rawListings: MockPlatformRawListing[]) {
        this.platform_id = config.platform_id;
        this.platform_name = config.platform_name;
        this.platform_kind = config.platform_kind;
        this.rawListings = rawListings;
    }

    async searchProducts(request: PlatformSearchProductsRequest = {}): Promise<MockPlatformRawListing[]> {
        const listings = this.rawListings
            .filter((raw) => matchesSearchRequest(raw, request))
            .sort((a, b) => a.mock_rank - b.mock_rank);

        return cloneData(typeof request.limit === 'number' ? listings.slice(0, request.limit) : listings);
    }

    async getListingDetail(platformListingId: string): Promise<PlatformListingDetail<MockPlatformRawListing> | null> {
        const listing = this.rawListings.find((raw) => (
            raw.id === platformListingId ||
            raw.platform_listing_id === platformListingId ||
            raw.external_product_id === platformListingId
        )) || null;

        if (!listing) return null;

        return {
            platform_id: this.platform_id,
            checked_at: listing.checked_at,
            collected_at: listing.collected_at,
            raw_listing: cloneData(listing)
        };
    }

    async getPriceAndStock(platformListingId: string): Promise<PlatformPriceAndStock | null> {
        const detail = await this.getListingDetail(platformListingId);
        if (!detail) return null;

        const listing = this.normalizeListing(detail.raw_listing);

        return {
            listing_id: listing.id,
            product_cluster_id: listing.product_cluster_id,
            platform_id: listing.platform_id,
            checked_at: listing.checked_at || listing.last_checked_at,
            collected_at: listing.collected_at || listing.last_checked_at,
            listed_price: listing.listed_price,
            sale_price: listing.sale_price,
            instant_discount: listing.instant_discount,
            public_coupon_discount: listing.public_coupon_discount,
            base_shipping_fee: listing.base_shipping_fee,
            shipping_fee: listing.shipping_fee,
            free_shipping_condition: listing.free_shipping_condition,
            free_shipping_price_basis: listing.free_shipping_price_basis || null,
            public_effective_price: listing.public_effective_price,
            currency: listing.currency,
            availability_status: listing.availability_status,
            options: cloneData(listing.options),
            freshness: cloneData(listing.freshness),
            source_status: listing.source_status,
            confidence: listing.confidence ?? detail.raw_listing.confidence
        };
    }

    normalizeListing(rawListing: MockPlatformRawListing): Listing {
        const baseListing = cloneData(rawListing.normalized_listing);
        const checkedAt = rawListing.checked_at;
        const collectedAt = rawListing.collected_at;
        const priceBreakdown = getPublicPriceBreakdown({
            listed_price: rawListing.price.listed_price,
            sale_price: rawListing.price.sale_price,
            instant_discount: rawListing.price.instant_discount,
            public_coupon_discount: rawListing.price.public_coupon_discount,
            base_shipping_fee: rawListing.price.base_shipping_fee,
            shipping_fee: rawListing.price.shipping_fee,
            free_shipping_condition: rawListing.price.free_shipping_condition,
            free_shipping_price_basis: rawListing.price.free_shipping_price_basis,
            availability_status: rawListing.stock.availability_status,
            benefits: baseListing.benefits
        });

        return {
            ...baseListing,
            platform_id: rawListing.platform_id,
            seller_id: rawListing.seller_id,
            external_product_id: rawListing.external_product_id,
            platform_listing_id: rawListing.platform_listing_id,
            seller_product_id: rawListing.seller_product_id,
            title: rawListing.title,
            product_url: rawListing.product_url,
            image_urls: cloneData(rawListing.image_urls),
            listed_price: rawListing.price.listed_price,
            sale_price: rawListing.price.sale_price,
            instant_discount: rawListing.price.instant_discount,
            public_coupon_discount: rawListing.price.public_coupon_discount,
            base_shipping_fee: rawListing.price.base_shipping_fee,
            shipping_fee: priceBreakdown.shipping_fee,
            free_shipping_condition: rawListing.price.free_shipping_condition,
            free_shipping_price_basis: rawListing.price.free_shipping_price_basis || null,
            public_effective_price: priceBreakdown.public_effective_price,
            currency: rawListing.price.currency,
            availability_status: rawListing.stock.availability_status,
            options: rawListing.stock.options.map((option) => ({
                ...option,
                last_checked_at: checkedAt,
                checked_at: option.checked_at || checkedAt,
                collected_at: option.collected_at || collectedAt
            })),
            benefits: cloneData(baseListing.benefits),
            last_checked_at: checkedAt,
            checked_at: checkedAt,
            collected_at: collectedAt,
            confidence: rawListing.confidence,
            freshness: cloneData(rawListing.freshness),
            source_status: rawListing.source_status
        };
    }
}

function createMockAdapters(): MockPlatformAdapter[] {
    const sourceListings = lookitListings.concat([
        createSpaOfficialListing(),
        createAuthorizedMockProviderListing()
    ]);

    return mockProviderConfigs.map((config) => {
        const rawListings = sourceListings
            .map((listing, index) => ({ listing, index }))
            .filter(({ listing }) => listing.platform_id === config.platform_id)
            .map(({ listing, index }) => standardListingToRaw(config, listing, index));

        return new MockPlatformAdapter(config, rawListings);
    });
}

export class MockPlatformAdapterRegistry {
    private readonly registrations: PlatformAdapterRegistration<MockPlatformRawListing>[];

    constructor(adapters: MockPlatformAdapter[], options: MockPlatformAdapterRegistryOptions = {}) {
        this.registrations = createMockPlatformAdapterRegistrations(adapters, options);
    }

    getAdapters(): MockPlatformAdapter[] {
        return this.getActivations()
            .filter((activation) => activation.active)
            .map((activation) => activation.adapter as MockPlatformAdapter);
    }

    getAdapter(platformId: string): MockPlatformAdapter | null {
        return this.getAdapters().find((adapter) => adapter.platform_id === platformId) || null;
    }

    getActivation(platformId: string): PlatformAdapterActivation<MockPlatformRawListing> | null {
        return this.getActivations().find((activation) => activation.adapter.platform_id === platformId) || null;
    }

    getActivations(): PlatformAdapterActivation<MockPlatformRawListing>[] {
        const registry = new PermissionGuardedPlatformAdapterRegistry(this.registrations);
        return registry.getActivations();
    }

    async searchProducts(request: PlatformSearchProductsRequest = {}): Promise<PlatformSearchProductsResult<MockPlatformRawListing>> {
        const pairs: Array<{ raw: MockPlatformRawListing; listing: Listing }> = [];

        for (const adapter of this.getAdapters()) {
            const rawListings = await adapter.searchProducts(request);
            rawListings.forEach((raw) => {
                pairs.push({
                    raw,
                    listing: adapter.normalizeListing(raw)
                });
            });
        }

        pairs.sort((a, b) => a.raw.mock_rank - b.raw.mock_rank);
        const limitedPairs = typeof request.limit === 'number' ? pairs.slice(0, request.limit) : pairs;

        return {
            requested_at: new Date().toISOString(),
            raw_listings: limitedPairs.map((pair) => cloneData(pair.raw)),
            listings: limitedPairs.map((pair) => cloneData(pair.listing))
        };
    }

    async searchListings(request: PlatformSearchProductsRequest = {}): Promise<Listing[]> {
        const result = await this.searchProducts(request);
        return result.listings;
    }

    async getListingDetail(listingId: string): Promise<PlatformListingDetail<MockPlatformRawListing> | null> {
        for (const adapter of this.getAdapters()) {
            const detail = await adapter.getListingDetail(listingId);
            if (detail) return detail;
        }

        return null;
    }

    async getPriceAndStock(listingId: string): Promise<PlatformPriceAndStock | null> {
        for (const adapter of this.getAdapters()) {
            const priceAndStock = await adapter.getPriceAndStock(listingId);
            if (priceAndStock) return priceAndStock;
        }

        return null;
    }
}

export interface MockPlatformAdapterRegistryOptions {
    authorizedMockProviderMode?: PlatformProviderMode;
    authorizedMockPermissionEvidence?: PermissionEvidence | null;
}

export function createMockPlatformAdapterRegistrations(
    adapters: MockPlatformAdapter[],
    options: MockPlatformAdapterRegistryOptions = {}
): PlatformAdapterRegistration<MockPlatformRawListing>[] {
    const runtimeConfig = runtimeAuthorizedMockProviderConfig();
    const authorizedProviderMode = options.authorizedMockProviderMode || runtimeConfig.providerMode;

    return adapters.map((adapter) => {
        const isAuthorizedMockProvider = adapter.platform_id === authorizedMockProviderPlatformId;

        return {
            adapter,
            providerMode: isAuthorizedMockProvider ? authorizedProviderMode : 'mock',
            permissionEvidence: isAuthorizedMockProvider ? options.authorizedMockPermissionEvidence || null : null,
            permissionRequirements: isAuthorizedMockProvider ? authorizedMockProviderPermissionRequirements : undefined
        };
    });
}

export const mockPlatformAdapters = createMockAdapters();
export const authorizedMockProvider = mockPlatformAdapters.find((adapter) => (
    adapter.platform_id === authorizedMockProviderPlatformId
)) as MockPlatformAdapter;

export function createMockPlatformAdapterRegistry(
    options: MockPlatformAdapterRegistryOptions = {}
): MockPlatformAdapterRegistry {
    return new MockPlatformAdapterRegistry(mockPlatformAdapters, options);
}

export const mockPlatformAdapterRegistry = createMockPlatformAdapterRegistry();

export function getMockPlatformAdapterCoverage() {
    return mockPlatformAdapters.map((adapter) => ({
        platform_id: adapter.platform_id,
        platform_name: adapter.platform_name,
        platform_kind: adapter.platform_kind
    }));
}
